/**
 * Computes the discount amount from a promo rule against the current booking amounts.
 * @param {Object} promo - Promo object with value, discount_type, apply_to_add_ons
 * @param {number} baseAmount - Guest subtotal (excluding fees and add-ons)
 * @param {number} addOnsTotal - Add-ons subtotal
 */
export const computeDiscountAmount = (promo, baseAmount = 0, addOnsTotal = 0) => {
  if (promo.value == null || !promo.discount_type) return promo.discountAmount ?? 0
  const applicableBase = baseAmount + (promo.apply_to_add_ons ? addOnsTotal : 0)
  if (promo.discount_type === "percentage") {
    return Math.max(0, (applicableBase * promo.value) / 100)
  }
  return Math.max(0, Math.min(promo.value, applicableBase))
}

/**
 * Resolves the promo objects whose discount should actually be applied to a booking.
 * A newly applied promo REPLACES the booking's original discount. When no new promo is
 * applied, edit mode falls back to the original discount stored on payment_summary.
 *
 * The returned array is shaped for computeDiscountAmount / calculateBookingTotal
 * ({ value, discount_type, apply_to_add_ons }).
 * @param {Array} appliedPromos - Live promos applied via PromoCodeInput
 * @param {Object} booking - The booking being edited (for its original discount)
 * @param {Boolean} isEdit - Whether this is an edit operation
 */
export const resolveEffectiveDiscountPromos = (appliedPromos = [], booking = {}, isEdit = false) => {
  if (appliedPromos.length > 0) return appliedPromos
  if (!isEdit) return []
  // The discount RULE comes from booking.discount_details (booking detail API). Fall back to
  // payment_summary for older shapes. Returning the rule (not a frozen amount) lets the discount
  // recompute against the live base when guests or add-ons change.
  const detail = booking?.discount_details
  const paymentSummary = booking?.payment_summary
  const value = detail?.value ?? paymentSummary?.discount_value
  const discountType = detail?.discount_type ?? paymentSummary?.discount_type
  if (discountType && value != null) {
    return [
      {
        value: Number(value),
        discount_type: discountType,
        apply_to_add_ons:
          detail?.applicability_rules?.apply_to_add_ons ??
          paymentSummary?.applicability_rules?.apply_to_add_ons ??
          paymentSummary?.apply_to_add_ons ??
          false
      }
    ]
  }
  return []
}
